import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { NOTIFICATION_TIMEOUT_LONG_6S } from '@modules/common/constants';
import { Typography } from '@mui/material';
import { UserInfo } from 'generated/graphql';
import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ADD_DASHBOARD_COLLABORATOR_PARAM } from '../constants';
import { getDashboardRoute } from '../helpers';
import { useDashboardStore } from '../store/useDashboardStore';
import { useUpdateDashboard } from './useUpdateDashboard';

export const useAddCollaboratorFromURL = () => {
  const navigate = useNavigate();
  const { showSuccessNotification } = useNotificationContext();
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const { updateDashboard, called } = useUpdateDashboard();

  const [searchParams] = useSearchParams();

  const collaboratorBase64JSON = searchParams.get(
    ADD_DASHBOARD_COLLABORATOR_PARAM,
  );

  const getCollaboratorFromParams = useCallback(() => {
    const decodedJSON = collaboratorBase64JSON
      ? decodeURIComponent(window.atob(collaboratorBase64JSON))
      : undefined;

    const collaboratorToAdd: UserInfo = decodedJSON
      ? JSON.parse(decodedJSON)
      : undefined;

    return collaboratorToAdd;
  }, [collaboratorBase64JSON]);

  const addCollaborator = useCallback(async () => {
    try {
      const collaboratorToAdd = getCollaboratorFromParams();

      if (!collaboratorToAdd || !dashboard.id) {
        return;
      }

      await updateDashboard({
        updateRequest: {
          id: dashboard.id,
          viewToken: dashboard.viewToken,
          createdBy: dashboard.createdBy.id,
          editToken: dashboard.editToken ?? '',
          editorsToAdd: [collaboratorToAdd.id],
        },
      });

      showSuccessNotification({
        contentComponent: () => (
          <Typography>
            "{dashboard.title}" has been shared with{' '}
            {collaboratorToAdd.firstName ?? collaboratorToAdd.lastName}{' '}
            successfully! They can now edit the Dashboard or invite others.
          </Typography>
        ),
        props: {
          isClosable: true,
          timeout: NOTIFICATION_TIMEOUT_LONG_6S,
        },
      });
      navigate(getDashboardRoute(dashboard), { replace: true });
    } catch (_) {}
  }, [
    dashboard,
    getCollaboratorFromParams,
    navigate,
    showSuccessNotification,
    updateDashboard,
  ]);

  useEffect(() => {
    if (called) {
      return;
    }
    addCollaborator();
  }, [addCollaborator, called]);
};
